import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getTranslation } from '../translations';
import { getInitialLanguage } from '../utils/languageDetection';
import Footer from '../components/Footer';
import './AuthPage.css';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [language, setLanguage] = useState(getInitialLanguage());

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      toast.error(language === 'de' ? 'Ungültiger Reset-Link' : 'Invalid reset link');
      navigate('/');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, navigate, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error(language === 'de' ? 'Passwort muss mindestens 6 Zeichen lang sein' : 'Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error(language === 'de' ? 'Passwörter stimmen nicht überein' : 'Passwords do not match');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      
      toast.success(language === 'de' ? 'Passwort erfolgreich zurückgesetzt! Sie können sich jetzt anmelden.' : 'Password reset successful! You can now login.');
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'de' ? 'Fehler beim Zurücksetzen des Passworts' : 'Failed to reset password');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Language Selector - Upper Right Corner */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <Select 
          value={language} 
          onValueChange={(value) => {
            setLanguage(value);
            localStorage.setItem('appLanguage', value);
          }}
        >
          <SelectTrigger style={{width: '180px', backgroundColor: 'white'}}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">🇬🇧 English</SelectItem>
            <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Main content - centered */}
      <div style={{ flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div className="auth-container">
          <div className="auth-card">
          <h2>{language === 'de' ? 'Passwort zurücksetzen' : 'Reset Your Password'}</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            {language === 'de' ? 'Geben Sie unten Ihr neues Passwort ein.' : 'Enter your new password below.'}
          </p>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <Label htmlFor="password">{language === 'de' ? 'Neues Passwort' : 'New Password'}</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={language === 'de' ? 'Mindestens 6 Zeichen' : 'At least 6 characters'}
                  required
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#666',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="confirmPassword">{language === 'de' ? 'Passwort bestätigen' : 'Confirm Password'}</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={language === 'de' ? 'Passwort erneut eingeben' : 'Re-enter your password'}
                  required
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#666',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading 
                ? (language === 'de' ? 'Wird zurückgesetzt...' : 'Resetting...') 
                : (language === 'de' ? 'Passwort zurücksetzen' : 'Reset Password')}
            </Button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
            >
              {language === 'de' ? '← Zurück zur Anmeldung' : '← Back to Login'}
            </Button>
          </div>
        </div>
        </div>
      </div>
      
      {/* Footer - Sticky at bottom */}
      <Footer language={language} />
    </div>
  );
}
